/**
 * Ввод платформенного refresh-токена standalone-пользователем.
 *
 * Введённый токен не принимается «на веру»: сразу за сохранением идёт обмен
 * refresh→access, потому что только он отличает рабочий токен от опечатки, от
 * мёртвого токена и от лежащей платформы. Три исхода разведены явно — молчаливого
 * «ну наверное сработало» здесь быть не может.
 */

import React, { useCallback, useState } from 'react';
import { Alert, Input, Modal, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { describePlatformError, isJwtLike } from '@aseller/platform-token';
import { platformTokenStore } from '../../shared/api/client';
import { useAuthStore } from '../../shared/session/authStore';

export interface PlatformTokenModalProps {
  open: boolean;
  onClose: () => void;
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * Текст отказа обмена. Порядок веток — от самой конкретной причины к общей:
 * 502 платформы описывает сам пакет (ключи `platform.unavailable` /
 * `platform.partialCreate`), остальное показывается с ИСХОДНЫМ сообщением, а не
 * прячется за «что-то пошло не так».
 *
 * Ветки «сначала войдите в модуль» здесь нет: обмен больше не закрыт модульным
 * гардом (S2b, задача 2), платформенный токен и есть вход. Отвергнутый платформой
 * refresh сюда не попадает вовсе — стор отвечает на него `null`, и это отдельный
 * текст `platform.tokenRejected`.
 */
function failureMessage(error: unknown, t: Translate): string {
  const platformText = describePlatformError(error, t);
  if (platformText !== null) return platformText;
  return t('platform.exchangeFailed', {
    reason: error instanceof Error ? error.message : String(error),
  });
}

const PlatformTokenModal: React.FC<PlatformTokenModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const translate = useCallback<Translate>(
    (key, params) => t(key, params ?? {}),
    [t],
  );
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    const token = value.trim();
    if (!isJwtLike(token)) {
      setError(translate('platform.invalidToken'));
      return;
    }

    setError(null);
    setSubmitting(true);
    platformTokenStore.setRefreshToken(token);
    try {
      const access = await platformTokenStore.getFreshAccessToken();
      if (access === null) {
        // Стор уже перевёл себя в `expired` (обмен ответил 401 refresh_token_invalid).
        setError(translate('platform.tokenRejected'));
        return;
      }
      // Личность могла измениться: до токена пользователь был анонимом.
      await useAuthStore.getState().fetchMe();
      setValue('');
      onClose();
    } catch (exchangeError) {
      setError(failureMessage(exchangeError, translate));
    } finally {
      setSubmitting(false);
    }
  }, [onClose, translate, value]);

  const handleCancel = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={open}
      title={translate('platform.modalTitle')}
      okText={translate('platform.submit')}
      cancelText={translate('platform.cancel')}
      confirmLoading={submitting}
      onOk={handleSubmit}
      onCancel={handleCancel}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Typography.Text type="secondary">{translate('platform.ctaHint')}</Typography.Text>
        <Input.TextArea
          aria-label={translate('platform.tokenLabel')}
          placeholder={translate('platform.tokenPlaceholder')}
          autoSize={{ minRows: 3, maxRows: 6 }}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        {error !== null && <Alert type="error" showIcon message={error} />}
      </Space>
    </Modal>
  );
};

export default PlatformTokenModal;
